import type { PlanRunStatus } from '@/constants/plan-run-status';
import type {
  PlanRunFailureProfile,
  TrajectoryPlanFailureReason,
  TransportVehiclePlanIssue,
} from '@/constants/plan-failure-reason';
import type { Country } from '@/constants/country';

/** Eén automatische planning-run, zoals de dagplanner die nodig heeft. */
export interface PlanRun {
  id: number;
  status: PlanRunStatus;
  /** Laadmoment van de run in epoch-ms; de placeholders staan alleen op deze dag. */
  startDate: number;
  /** De deelnemende transportvoertuigen: precies de rijen die een placeholder krijgen. */
  transportVehicleIds: number[];
  trajectoryIds: number[];
  /**
   * De opdrachten van de deelnemende trajecten. Komt van de server omdat de opdrachtenlijst de traject-ids van
   * haar rijen niet kent en het daar dus niet zelf kan afleiden. Alleen gevuld zolang de run loopt.
   */
  assignmentIds: number[];
  /** De auto's van die opdrachten: gewicht en lengte zijn invoer voor de berekening. */
  vehicleIds: number[];
  /**
   * De standplaats-relaties van de deelnemende vrachtwagens. Alleen daarvan gaan het bezoekadres en het
   * deactiveren op slot; de rest van zo'n relatie blijft bewerkbaar.
   */
  stationRelationIds: number[];
  /** Aantal aangemaakte ritten; null zolang het resultaat nog niet verwerkt is. */
  createdTransportCount: number | null;
  /** Trajecten die na het verwerken op Te plannen zijn blijven staan. */
  unplannedTrajectoryIds: number[];
  /** Onder welk profiel deze afloop valt; bepaalt welke melding of dialoog de planner krijgt. */
  failureProfile: PlanRunFailureProfile;
  /** Ritdelen waarvan afstand en rijtijd geschat zijn, en het totaal. */
  estimatedLegCount: number | null;
  totalLegCount: number | null;
  /**
   * Of de ingelogde gebruiker deze run zelf startte. De samenvatting en de foutmelding zijn alleen voor hem:
   * de statusopvraging draait bij elke planner, dus zonder dit onderscheid krijgt iedereen de afloop van
   * elkaars run te zien. Dát er een run loopt blijft wél voor iedereen zichtbaar.
   */
  startedByMe: boolean;
  /** Voortgang uit de laatste statusopvraging bij APE, of onze eigen fase tijdens het verwerken. */
  phase: string | null;
  /** Sinds wanneer de fase loopt, in epoch-ms; waarmee de balk in de oplosfase op het zoekbudget meeloopt. */
  phaseSince: number | null;
  queuePosition: number | null;
  /** Alleen gevuld bij een mislukte run. */
  error: string | null;
  /**
   * Het vangnet sloot de run af omdat ónze verwerking stopte terwijl de engine nog rekende (werkitem 7456).
   * Zelfde profiel als een time-out, maar met een eigen melding.
   */
  processGone: boolean;
  startedAt: number;
  completedAt: number | null;
  createdByName: string | null;
  /**
   * Wie de run afbrak, en of dat de ingelogde gebruiker zelf was. Alleen gevuld bij een afgebroken run.
   * Wie zelf afbrak kreeg zijn bevestiging al bij het indrukken; een collega die de run zag lopen hoort juist
   * te horen dát en door wie hij gestopt is.
   */
  cancelledByMe: boolean;
  cancelledByName: string | null;
}

/** Hoe het afbreken afliep. Spiegelt CancelPlanRunOutcome in de backend. */
export enum CancelPlanRunOutcome {
  Cancelled = 0,
  /** De run was al klaar, mislukt of eerder afgebroken; de bestaande uitkomst is ongemoeid gelaten. */
  AlreadyFinished = 1,
  /**
   * Het resultaat wordt al verwerkt tot concept-transporten; afbreken is geweigerd omdat die concepten
   * anders onder een "afgebroken"-uitkomst zouden blijven staan. De run loopt gewoon af.
   */
  ResultBeingApplied = 2,
}

export interface CancelPlanRunResponse {
  outcome: CancelPlanRunOutcome;
  /** De stand van de run ná de aanroep; bij AlreadyFinished dus de uitkomst die er al lag. */
  status: PlanRunStatus;
}

/**
 * De samenvatting van één run: wat er gepland is en wat er om welke reden bleef liggen. Wordt één keer
 * opgehaald wanneer de planner de dialoog opent, niet op het poll-pad.
 */
export interface PlanRunSummary {
  planRunId: number;
  status: PlanRunStatus;
  failureProfile: PlanRunFailureProfile;
  /** De M in "N van de M trajecten ingepland". */
  trajectoryCount: number;
  /** De N. */
  plannedTrajectoryCount: number;
  createdTransportCount: number;
  estimatedLegCount: number;
  totalLegCount: number;
  /** Referentiecode van de run, voor een melding aan beheer bij een technische fout. */
  reference: string;
  unplannedTrajectories: PlanRunUnplannedTrajectory[];
}

export interface PlanRunUnplannedTrajectory {
  trajectoryId: number;
  assignmentId: number;
  licensePlate: string;
  /** Voor de kentekenweergave: die tekent de plaat per land en valt terug op het chassisnummer. */
  country: Country | null;
  chassisNumber: string | null;
  brand: string | null;
  model: string | null;
  pickUpCity: string | null;
  dropOffCity: string | null;
  reason: TrajectoryPlanFailureReason;
  /** Of het traject op dit moment nog te plannen is; een collega kan het inmiddels hebben afgehandeld. */
  isStillToPlan: boolean;
}

/** De uitkomst van de controle vóór verzenden (profiel V). */
export interface PlanRunCheck {
  canStart: boolean;
  /** Of doorgaan zonder de probleemtrajecten mag: alleen als er genoeg overblijft én de voertuigen kloppen. */
  canContinueWithoutProblemTrajectories: boolean;
  plannableTrajectoryIds: number[];
  trajectoryIssues: PlanRunTrajectoryIssue[];
  transportVehicleIssues: PlanRunTransportVehicleIssue[];
  /**
   * De selectie is groter dan de planning-engine aanneemt. Apart van de problemen per traject: dit gaat niet
   * over een ontbrekend gegeven maar over de omvang, en de planner lost het op door minder te selecteren.
   */
  tooManyTrajectories: boolean;
  tooManyTransportVehicles: boolean;
  maxTrajectories: number;
  maxTransportVehicles: number;
}

export interface PlanRunTrajectoryIssue {
  trajectoryId: number;
  /** Voor de link naar het scherm waar dit te repareren is. */
  assignmentId: number;
  licensePlate: string;
  country: Country | null;
  chassisNumber: string | null;
  reason: TrajectoryPlanFailureReason;
}

export interface PlanRunTransportVehicleIssue {
  transportVehicleId: number;
  name: string;
  issue: TransportVehiclePlanIssue;
}

export interface ActivePlanRunResponse {
  /** De meest recente run van de organisatie, of null als er nooit één was. */
  planRun: PlanRun | null;
  /** De run is Pending of Running: alleen dán placeholders tonen en automatisch plannen op slot. */
  isActive: boolean;
  /** Servertijd in epoch-ms, voor een voortgangsweergave die niet op de browserklok leunt. */
  serverTime: number;
}
